<script setup lang="ts">
import { computed, provide } from "vue";
import SwitchInput from "@/ui/components/switch/switch-input.vue";
import Props from "@/ui/components/switch/switch-types";

// The template has two root nodes (the switch and the error span), so Vue
// can't auto-inherit fallthrough attributes. Forward them explicitly onto the
// clickable root below so attributes like data-testid actually land in the DOM.
defineOptions({ inheritAttrs: false });

const props = defineProps<Props>();
const check = defineModel<boolean>();
const loading = defineModel<boolean>("loading");

/** Function to handle the switch toggle. */
const toggle = () => {
	if (loading.value || props.disabled) return;

	check.value = !check.value;
};

provide("switch-props", props);

const currentError = computed(() => {
	if (!props.errorMessages) return null;
	if (typeof props.errorMessages === "string") {
		return props.errorMessages;
	}

	return props.errorMessages.at(0) ?? null;
});

const showErrors = computed(() => {
	return (
		(currentError.value && !props.hideDetails) ||
		(props.hideDetails === "auto" && currentError.value)
	);
});

const iconPosition = computed(() => props.iconPosition ?? "left");
const labelPosition = computed(() => props.labelPosition ?? "left");
</script>

<template>
	<div
		v-bind="$attrs"
		class="d-inline-flex align-center ga-2 cursor-pointer"
		@click="toggle"
	>
		<slot name="left">
			<v-icon
				v-if="props.icon && iconPosition === 'left'"
				:icon="props.icon"
				:key="props.icon"
			/>

			<v-label
				v-if="props.label && labelPosition === 'left'"
				:text="props.label"
				class="ga-2 text-medium-emphasis cursor-pointer"
				style="opacity: 1; user-select: none"
			/>
		</slot>

		<switch-input v-model="check" v-model:loading="loading" />

		<slot name="right">
			<v-label
				v-if="props.label && labelPosition === 'right'"
				:text="props.label"
				class="ga-2 text-medium-emphasis cursor-pointer"
				style="opacity: 1; user-select: none"
			/>

			<v-icon
				v-if="props.icon && iconPosition === 'right'"
				:icon="props.icon"
				:key="props.icon"
			/>
		</slot>
	</div>
	<!--TODO: Fancy animations-->
	<span class="errors" v-if="showErrors">
		{{ currentError }}
	</span>
</template>

<style scoped>
.errors {
	color: red;
}
</style>
